import { HumanResourcesItem } from '@/app/models/detailsRH';
import { useUniteDetailsHumanResourcesMutation } from '@/app/services/detailsHRApi';
import DrawerButtons from '@/components/common/DrawerButtons';
import ModalPageTitle from '@/components/common/ModalPageTitle';
import { usePeriod } from '@/context/PeriodContext';
import { Box, FormControl, MenuItem, Select, SelectChangeEvent, Typography, useTheme } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

interface NewUnionProps {
  selectItem: HumanResourcesItem[] | null;
  setSelectItem: (id: HumanResourcesItem[]) => void;
  setDrawerOpen: (open: boolean) => void;
}

export default function NewUnion({ selectItem, setDrawerOpen, setSelectItem }: NewUnionProps) {
  const theme = useTheme();
  const { t } = useTranslation(['humanResource', 'common']);
  const period = usePeriod();

  const [selectedTarget, setSelectedTarget] = useState<HumanResourcesItem | null>(null);

  const [unit] = useUniteDetailsHumanResourcesMutation();

  const handleUnite = async () => {
    await toast.promise(
      unit({
        ids: (selectItem ?? []).map((item) => item.id),
        period: period.selectedPeriod?.date as string,
        principalDetailId: selectedTarget?.id as number,
      }).unwrap(),
      {
        pending: t('common:processing.wait.a.moment') as string,
        success: t('common:union.success') as string,
        error: t('common:processing.failed') as string,
      },
      {
        position: toast.POSITION.TOP_RIGHT,
      },
    );
    setSelectItem([]);
    setDrawerOpen(false);
  };

  const handleCancel = () => setDrawerOpen(false);

  const handleChange = (e: SelectChangeEvent<number>) => {
    const selectedId = Number(e.target.value);
    const found = selectItem?.find((item) => item.id === selectedId) ?? null;
    setSelectedTarget(found);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 24px)',
        maxHeight: 'calc(100vh - 24px)',
        flexGrow: 'inherit',
      }}
    >
      <Box sx={{ flexGrow: 1 }}>
        <ModalPageTitle title={t('unite.records')} />

        <Box sx={{ mt: 2 }}>
          <Box sx={{ minWidth: theme.spacing(31), display: 'flex', gap: 2, alignItems: 'center' }}>
            <Typography component="p" variant="fieldHeader" sx={{ mt: 1 }}>
              {t('common:collaborator')}
            </Typography>

            <FormControl sx={{ width: theme.spacing(30) }}>
              <Select
                displayEmpty
                value={selectedTarget?.id ?? ''}
                onChange={handleChange}
                sx={{ height: 32, fontSize: 14 }}
                renderValue={() => (selectedTarget ? selectedTarget.name : <em>{t('select.collaborator')}</em>)}
              >
                <MenuItem value="">
                  <em>{t('select.collaborator')}</em>
                </MenuItem>

                {(selectItem ?? []).map((item) => (
                  <MenuItem key={item.id} value={item.id} sx={{ fontSize: 13 }}>
                    {item.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Typography component="p" sx={{ mt: 1 }}>
            {t('select.collaborator.to.receive.records.when.merging')}
          </Typography>
        </Box>
      </Box>

      <Box sx={{ mt: 'auto' }}>
        <DrawerButtons onSave={handleUnite} onCancel={handleCancel} disabled={!selectedTarget} />
      </Box>
    </Box>
  );
}
